import './index.css'

import React, { useEffect, useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import {
  checkLoginUrl,
  resetPassword,
} from '@disco/state-management/actions/UserActions'
import type { UserReducer } from '@disco/state-management/reducers/user'
// import type { User } from '@disco/state-management/reducers/user'
import getReferrer from '@disco/utils/getReferrer'
import ChangePassword from 'components/common/change-password'
import LoginMessage from 'components/common/login-message'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import LoginError from 'components/utils/login-error'
import { useTranslation } from '@disco/i18n/use-translation'

const PasswordReset = ({
  user,
  // Used for testing in a storybook
  defaultDisplayReturnTo,
}: {
  user: UserReducer
  defaultDisplayReturnTo?: string
}): React.ReactNode => {
  const { t } = useTranslation('accounts')
  const dispatch = useDispatch()
  const [displayReturnTo, setDisplayReturnTo] = useState(defaultDisplayReturnTo)
  const {
    loginUrlChecking,
    loginUrlCheckError,
    passwordChanging,
    passwordChangeError,
  } = user
  useEffect(() => {
    dispatch(checkLoginUrl(getReferrer()))
  }, [])
  useEffect(() => {
    if (user.passwordChanged) {
      let returnUrl

      try {
        returnUrl = new URL(window.location.href).searchParams.get('return')
      } catch (err) {
        console.error('URL parsing error', err)
      }

      setDisplayReturnTo(returnUrl || '/login')
    }
  }, [user.passwordChanged])

  const _onPasswordChange = (password: string) => {
    dispatch(resetPassword(password, getReferrer()))
  }

  const passwordStrength =
    user.business?.public_metadata.password_strength || 'standard'
  let authState = <span />

  if (loginUrlChecking) {
    authState = <span className="PasswordReset_Text">Authorizing...</span>
  } else if (loginUrlCheckError) {
    authState = (
      <div className="Login_ValidationMessage">
        <LoginError
          passwordStrength={passwordStrength}
          errorContext="link"
          error={loginUrlCheckError}
          suggestContactingSupport
          redirectIfUrlExpired="/forgot-password"
        />
      </div>
    )
  } else {
    authState = (
      <ChangePassword
        type="login"
        passwordStrength={passwordStrength}
        passwordChangeInProgress={passwordChanging}
        passwordChangeError={passwordChangeError}
        onPasswordChange={_onPasswordChange}
      />
    )
  }

  return (
    <LoginMessage
      helmet={t('accounts:resetPasswordPageTitle', 'Reset password')}
      title={
        displayReturnTo
          ? t(
              'accounts:passwordChangedSuccessfully',
              'Password changed successfully!',
            )
          : t('accounts:selectNewPassword', 'Select New Password')
      }
    >
      {displayReturnTo ? (
        <ButtonContainer centered>
          <Button
            large
            appearance="primary"
            link={displayReturnTo}
            caption={t('accounts:signIn', 'Sign in')}
          />
        </ButtonContainer>
      ) : (
        authState
      )}
    </LoginMessage>
  )
}

export default PasswordReset
